import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export * from './logger';
export * from './helpers';
export * from './encryption';
export * from './crypto-calculation';
export * from './crypto-format';
export * from './validation';
export * from './parsers';
export * from './validation-reasons';
export * from './session-metadata';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date));
}

export function formatDateTime(date: Date | string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date(date));
}

export function formatCurrency(
  amount: number,
  currency: string = 'USD',
): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

export function truncateAddress(
  address: string,
  start: number = 6,
  end: number = 4,
): string {
  if (address.length <= start + end) return address;
  return `${address.slice(0, start)}...${address.slice(-end)}`;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function generateRandomString(length: number): string {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Calculate credit cost for an extraction task
 * This matches the backend calculation in ExtractionService.calculateCreditCost
 *
 * @param urlCount - Number of URLs to extract
 * @param autoValidate - Whether to auto-validate results
 * @param maxDepth - Maximum crawl depth
 * @param aiPrioritization - Whether AI prioritization is enabled
 * @param maxToken - Optional maximum token limit (caps the cost)
 * @returns Calculated credit cost
 */
export function calculateCreditCost(
  urlCount: number,
  autoValidate: boolean,
  maxDepth: number,
  aiPrioritization: boolean,
  maxToken?: number,
): number {
  // Base cost per URL (matches backend BASE_COST_PER_URL = 10)
  let cost = 10 * urlCount;

  // Add validation cost (matches backend VALIDATION_COST_MULTIPLIER = 1.5)
  if (autoValidate) {
    cost *= 1.5;
  }

  // Add depth cost (matches backend DEPTH_COST_MULTIPLIER = 0.2)
  const depth = maxDepth || 1;
  cost *= 1 + (depth - 1) * 0.2;

  // Add AI prioritization cost (matches backend AI_PRIORITIZATION_COST = 10)
  if (aiPrioritization) {
    cost += 10;
  }

  // Apply max token limit if specified (matches backend logic)
  if (maxToken && cost > maxToken) {
    cost = maxToken;
  }

  return Math.ceil(cost);
}
